import { chatStateAtom } from '@/atoms/chatAtoms';
import { ChatCompletionToast } from '@/components/chat/components/ChatCompletionToast';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import type { TimelineEntry } from '@/components/chat/types/types';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { stripLocalePathPrefix } from '@/utils/i18n/supportedLocales';
import { useStore } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useRef } from 'react';
import sanitizeHtml from 'sanitize-html';
import { toast } from 'sonner';

interface UseChatCompletionNotificationOptions {
  /** Chat title from chatMeta (passed from useChatBackend) */
  chatTitle?: string;
  /** Whether this is a sessionless chat (no linked session) */
  isSessionlessChat?: boolean;
}

const stripHtml = (html: string): string => {
  const text = sanitizeHtml(html, { allowedTags: [] });
  return text.replace(/\s+/g, ' ').trim();
};

const truncateText = (text: string, maxLength = 100): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

const getLastAssistantResponse = (timeline: TimelineEntry[]): string | null => {
  if (!timeline || timeline.length === 0) return null;

  // Find the last text_item to identify the latest response's groupId
  let lastTextItemGroupId: string | undefined;
  for (let i = timeline.length - 1; i >= 0; i--) {
    const entry = timeline[i];
    if (entry && entry.type === 'text_item') {
      lastTextItemGroupId = entry.groupId;
      break;
    }
  }

  // Collect all text_items from the latest response group
  const responseTextItems: string[] = [];
  for (const entry of timeline) {
    if (entry && entry.type === 'text_item') {
      if (!lastTextItemGroupId || entry.groupId === lastTextItemGroupId) {
        responseTextItems.push(entry.content);
      }
    }
  }

  // Concatenate all text content and strip HTML to get actual content
  const fullContent = responseTextItems.join('');
  const cleanContent = stripHtml(fullContent);

  return cleanContent || null;
};

/** Pathname without a leading locale segment (e.g. `/en/scribe/chat/x` → `/scribe/chat/x`). */
const isViewingThisChatPage = (pathname: string, chatId: string): boolean => {
  const path = stripLocalePathPrefix(pathname);
  return path === `/chat/${chatId}` || path === `/scribe/chat/${chatId}`;
};

export const useChatCompletionNotification = (
  chatId: string | null,
  options: UseChatCompletionNotificationOptions = {}
) => {
  const { chatTitle, isSessionlessChat = true } = options;
  const router = useRouter();
  const store = useStore();
  const routerRef = useRef(router);
  const { isAuthenticated } = useIsAuthenticated();
  const { trackToastShown } = useEvidenceAnalytics();

  routerRef.current = router;

  const handleStreamComplete = useCallback(() => {
    if (!chatId) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    // Don't show toast for in-session chats (only show for sessionless Evidence chats)
    if (!isSessionlessChat) {
      return;
    }

    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    if (currentPath.startsWith('/chat/embed')) {
      return;
    }

    // Check if the user is already viewing this chat page (including locale-prefixed routes such as /en/scribe/chat/{id}).
    if (isViewingThisChatPage(currentPath, chatId)) {
      return;
    }

    const freshChatState = store.get(chatStateAtom);
    const stateKey = `chat-${chatId}`;
    const session = freshChatState[stateKey];

    if (!session) {
      return;
    }

    const lastResponse = getLastAssistantResponse(session.timeline);

    if (lastResponse) {
      const truncatedResponse = truncateText(lastResponse);
      const toastTitle = chatTitle || 'Chat complete';

      // Track toast shown
      trackToastShown(chatId);

      toast.custom(
        (toastId) => {
          return (
            <ChatCompletionToast
              chatId={chatId}
              title={toastTitle}
              description={truncatedResponse}
              onDismiss={() => toast.dismiss(toastId)}
            />
          );
        },
        {
          duration: 5000,
        }
      );
    }
  }, [chatId, store, chatTitle, isSessionlessChat, isAuthenticated, trackToastShown]);

  return handleStreamComplete;
};
